import { useState } from "react";
import {
  IconButton,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
} from "@mui/material";

import FavoriteIcon from "@mui/icons-material/Favorite";
import CommentIcon from "@mui/icons-material/Comment";
import RepeatIcon from "@mui/icons-material/Repeat";
import LinkIcon from "@mui/icons-material/Link";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ImageIcon from "@mui/icons-material/Image";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import TranslateIcon from "@mui/icons-material/Translate";
import { renderPlatformIcon } from "./PlatformIcon";


const formatDatetimeRaw = (dateString) => {
  if (!dateString) return "Unknown date";
  // Strip timezone suffix so the displayed time matches exactly what the API returned
  const clean = dateString.replace(/[zZ]$/, "").replace(/[+-]\d{2}:\d{2}$/, "");
  const d = new Date(clean);
  if (isNaN(d)) return dateString;
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatDate = (dateString) => {
  if (!dateString) return "Unknown";
  const isAlreadyIST = /\+05:30$/.test(dateString);
  let formattedDate = dateString;
  if (!isAlreadyIST && !/[zZ]|[+-]\d{2}:\d{2}$/.test(dateString)) {
    formattedDate = dateString + "Z";
  }
  const str = new Date(formattedDate).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return `${str} IST`;
};

const getPlatformColor = (platform) => {
  const colors = {
    twitter: "#ffffff",
    facebook: "#E9F1FB",
    instagram: "#FDECEF",
    threads: "#ffffff",
    youtube: "#FFEBEB",
    default: "#64748b",
    reddit: "#FF4500",
  };
  return colors[platform?.toLowerCase()] || colors.default;
};

const getPlatformColorChip = (platform) => {
  const colors = {
    twitter: "#1DA1F2",
    facebook: "#4267B2",
    instagram: "#E4405F",
    linkedin: "#0077B5",
    youtube: "#FF0000",
    default: "#64748b",
    reddit: "#FF4500",
  };
  return colors[platform?.toLowerCase()] || colors.default;
};

const SENTIMENT_CONFIG = {
  Positive: { color: "#15803d", bg: "#dcfce7", border: "#86efac" },
  Negative: { color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
  Neutral:  { color: "#64748b", bg: "#f1f5f9", border: "#cbd5e1" },
};

const LabelRow = ({ label, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
    <Typography
      variant="caption"
      sx={{
        color: "#9ca3af",
        fontFamily: "Host Grotesk, sans-serif",
        fontSize: "0.72rem",
        fontWeight: 600,
        width: 88,
        flexShrink: 0,
        pt: 0.2,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const AnalysisPanel = ({ analysis, languages, topicType }) => {
  const sentiment = analysis?.sentiment;
  const section = analysis?.section;
  const sentimentConf = SENTIMENT_CONFIG[sentiment?.prediction] || SENTIMENT_CONFIG.Neutral;
  const scores = sentiment?.scores || {};

  const showSentiment = topicType === "sentiment";
  const showSection = topicType === "section";

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: "#f5f3ff",
        borderRadius: "10px",
        border: "1px solid #e9d5ff",
      }}
    >
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: "#7c3aed",
            fontFamily: "Host Grotesk, sans-serif",
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          AI Analysis
        </Typography>
        {languages && (
          <Chip
            label={languages}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.65rem",
              fontFamily: "Host Grotesk, sans-serif",
              backgroundColor: "#ede9fe",
              color: "#6d28d9",
              fontWeight: 600,
              border: "1px solid #c4b5fd",
            }}
          />
        )}
      </Box>

      {/* Sentiment Analysis */}
      {showSentiment && sentiment && (
        <Box>
          <LabelRow label="Prediction:">
            <Box
              sx={{
                display: "inline-flex",
                px: 1.2,
                py: 0.25,
                borderRadius: "999px",
                backgroundColor: sentimentConf.bg,
                border: `1px solid ${sentimentConf.border}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: sentimentConf.color, fontFamily: "Host Grotesk, sans-serif", fontSize: "0.75rem" }}
              >
                {sentiment.prediction}
              </Typography>
            </Box>
          </LabelRow>

          {sentiment.subjectivity && (
            <LabelRow label="Subjectivity:">
              <Box
                sx={{
                  display: "inline-flex",
                  px: 1.2,
                  py: 0.25,
                  borderRadius: "999px",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bae6fd",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#0369a1", fontFamily: "Host Grotesk, sans-serif", fontSize: "0.75rem" }}
                >
                  {sentiment.subjectivity.charAt(0).toUpperCase() + sentiment.subjectivity.slice(1)}
                </Typography>
              </Box>
            </LabelRow>
          )}

          {/* {Object.keys(scores).length > 0 && (
            <LabelRow label="Scores:">
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {Object.entries(scores).map(([key, val]) => {
                  const conf = SENTIMENT_CONFIG[key] || SENTIMENT_CONFIG.Neutral;
                  return (
                    <Typography
                      key={key}
                      variant="caption"
                      sx={{ fontFamily: "Host Grotesk, sans-serif", fontSize: "0.75rem", color: conf.color, fontWeight: 600 }}
                    >
                      {key}: {val}%
                    </Typography>
                  );
                })}
              </Box>
            </LabelRow>
          )} */}
        </Box>
      )}

      {/* Section Analysis */}
      {showSection && section && (
        <Box>
          {/* {section.blocked !== undefined && (
            <LabelRow label="Blocked:">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {section.blocked ? (
                  <>
                    <BlockIcon sx={{ fontSize: "0.85rem", color: "#dc2626" }} />
                    <Typography variant="caption" sx={{ color: "#dc2626", fontFamily: "Host Grotesk, sans-serif", fontSize: "0.75rem", fontWeight: 700 }}>
                      Yes
                    </Typography>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon sx={{ fontSize: "0.85rem", color: "#15803d" }} />
                    <Typography variant="caption" sx={{ color: "#15803d", fontFamily: "Host Grotesk, sans-serif", fontSize: "0.75rem", fontWeight: 700 }}>
                      No
                    </Typography>
                  </>
                )}
              </Box>
            </LabelRow>
          )} */}

          {section.sections?.length > 0 && (
            <LabelRow label="Sections:">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {section.sections.map((s, i) => (
                  <Chip
                    key={i}
                    label={s}
                    sx={{
                      height: 26,
                      fontSize: "0.8rem",
                      fontFamily: "Host Grotesk, sans-serif",
                      backgroundColor: "#fdf4ff",
                      color: "#86198f",
                      border: "1px solid #f0abfc",
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            </LabelRow>
          )}

          {section.reason && (
            <LabelRow label="Reason:">
              <Typography
                variant="caption"
                sx={{
                  color: "#475569",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  display: "block",
                }}
              >
                {section.reason}
              </Typography>
            </LabelRow>
          )}
        </Box>
      )}
    </Box>
  );
};

const TEXT_LIMIT = 260;

export const FeedItem = ({ item, index, showTranslation = false, topicType }) => {
  const [expanded, setExpanded] = useState(false);
  const [imgError, setImgError] = useState(false);

  const hasAnalysis = !!item?.analysis;
  const originalText = item?.text || item?.content || "";
  const hasTranslation = !!item?.translation && item.translation !== originalText;
  const displayContent = showTranslation && hasTranslation
    ? item.translation
    : (originalText || "No content available");

  const isLong = displayContent.length > TEXT_LIMIT;
  const visibleContent = expanded || !isLong
    ? displayContent
    : displayContent.slice(0, TEXT_LIMIT);

  const hasImage = !!item.screenshotUrl && item.screenshotUrl !== "n/a" && !imgError;

  // Prefer analysis-extracted hashtags for processed data, fall back to raw item.hashtags
  const hashtags = item.analysis?.section?.hashtags?.length > 0
    ? item.analysis.section.hashtags
    : (item.hashtags || []);

  return (
    <Card
      sx={{
        mb: 3,
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: getPlatformColor(item.platform),
              width: 48,
              height: 48,
              mr: 2,
              fontSize: "1.2rem",
            }}
          >
            {renderPlatformIcon
              ? renderPlatformIcon(item.platform)
              : (item.postOwner?.charAt(0) || item.platform?.charAt(0) || "U").toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "700", color: "#1e293b", fontFamily: "Host Grotesk, sans-serif" }}
              >
                {item.postOwner || "Unknown User"}
              </Typography>
              <Chip
                label={item.platform || "unknown"}
                size="small"
                sx={{
                  bgcolor: getPlatformColorChip(item.platform),
                  color: "white",
                  fontFamily: "Host Grotesk, sans-serif",
                  fontSize: "0.75rem",
                  height: "20px",
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                fontFamily: "Host Grotesk, sans-serif",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: "0.8rem" }} />
              {formatDatetimeRaw(item.datetime)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "Host Grotesk, sans-serif" }}>
              Post ID: {item._id?.slice(-8) || index}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: "0.7rem", color: "#94a3b8" }} />
              <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "Host Grotesk, sans-serif" }}>
                {formatDate(item.crawlTime)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ mb: 2 }}>
          {showTranslation && hasTranslation && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
              <TranslateIcon sx={{ fontSize: "0.8rem", color: "#0369a1" }} />
              <Typography variant="caption" sx={{ color: "#0369a1", fontFamily: "Host Grotesk, sans-serif", fontWeight: 600, fontSize: "0.7rem" }}>
                Translated
              </Typography>
            </Box>
          )}
          <Typography
            variant="body1"
            sx={{ color: "#374151", fontFamily: "Host Grotesk, sans-serif", lineHeight: 1.6, mb: isLong ? 0.5 : 2 }}
          >
            {visibleContent}{!expanded && isLong ? "…" : ""}
          </Typography>
          {isLong && (
            <Button
              size="small"
              onClick={() => setExpanded((v) => !v)}
              disableRipple
              sx={{
                fontFamily: "Host Grotesk, sans-serif",
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "none",
                color: "#6366f1",
                p: 0,
                mb: 2,
                minWidth: 0,
                background: "none",
                "&:hover": { background: "none", textDecoration: "underline" },
              }}
            >
              {expanded ? "Show less" : "Read more"}
            </Button>
          )}

          {item.metadata && item.metadata !== originalText && (
            <Box sx={{ p: 2, bgcolor: "#f8fafc", borderRadius: "8px", borderLeft: "4px solid #3b82f6", mb: 2 }}>
              <Typography variant="body2" sx={{ color: "#64748b", fontFamily: "Host Grotesk, sans-serif", fontStyle: "italic" }}>
                {item.metadata}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Inline screenshot image */}
        {hasImage && (
          <Box
            sx={{
              mb: 2,
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              cursor: "pointer",
              "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
              transition: "box-shadow 0.2s ease",
            }}
            onClick={() => window.open(item.screenshotUrl, "_blank")}
          >
            <img
              src={item.screenshotUrl}
              alt="Post screenshot"
              onError={() => setImgError(true)}
              style={{
                width: "100%",
                maxHeight: "320px",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        )}

        {/* Hashtags — above analysis panel */}
        {hashtags.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
            {hashtags.map((tag, i) => (
              <Chip
                key={i}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: "0.7rem",
                  fontFamily: "Host Grotesk, sans-serif",
                  color: "#6366f1",
                  borderColor: "#a5b4fc",
                }}
              />
            ))}
          </Box>
        )}

        {/* Media & Links */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {item.link && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<LinkIcon />}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontFamily: "Host Grotesk, sans-serif", textTransform: "none", fontSize: "0.75rem" }}
            >
              View Original
            </Button>
          )}
          {item.useHandle && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonIcon />}
              href={item.useHandle}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontFamily: "Host Grotesk, sans-serif", textTransform: "none", fontSize: "0.75rem" }}
            >
              Profile
            </Button>
          )}
          {item.screenshotUrl && item.screenshotUrl !== "n/a" && imgError && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ImageIcon />}
              href={item.screenshotUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontFamily: "Host Grotesk, sans-serif", textTransform: "none", fontSize: "0.75rem" }}
            >
              Screenshot
            </Button>
          )}
        </Box>

        {/* Engagement Metrics */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pt: 2,
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" sx={{ color: "#ef4444" }}>
                <FavoriteIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "Host Grotesk, sans-serif" }}>
                {item.likes || "0"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton size="small" sx={{ color: "#3b82f6" }}>
                <CommentIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "Host Grotesk, sans-serif" }}>
                {item.replies || item?.comments?.length || "0"}
              </Typography>
            </Box>
            {item?.platform !== "reddit" && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="small" sx={{ color: "#10b981" }}>
                  {item.platform === "youtube" ? <VisibilityIcon fontSize="small" /> : <RepeatIcon fontSize="small" />}
                </IconButton>
                <Typography variant="caption" sx={{ color: "#64748b", fontFamily: "Host Grotesk, sans-serif" }}>
                  {item.platform === "youtube" ? item.views || "0" : item.retweets || "0"}
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ textAlign: "right" }}>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "Host Grotesk, sans-serif", display: "block" }}>
              Added by: {item.created_by?.username || item.createdBy?.username || "Unknown"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8", fontFamily: "Host Grotesk, sans-serif" }}>
              Topic ID: {item.topic_id?.slice(-8) || "N/A"}
            </Typography>
          </Box>
        </Box>

        {/* Media Presence */}
        {item.mediaPresent && item.mediaPresent !== "n/a" && (
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <ImageIcon sx={{ color: "#8b5cf6", fontSize: "1rem" }} />
            <Typography variant="caption" sx={{ color: "#8b5cf6", fontFamily: "Host Grotesk, sans-serif", fontWeight: "500" }}>
              The original post contains an image or video.
            </Typography>
          </Box>
        )}

        {/* AI Analysis Panel */}
        {hasAnalysis && (
          <AnalysisPanel
            analysis={item.analysis}
            languages={item.languages}
            topicType={topicType}
          />
        )}
      </CardContent>
    </Card>
  );
};
